import Service from '@ember/service';
import { service } from '@ember/service';
import { queryManager } from 'ember-apollo-client';
import { tracked } from '@glimmer/tracking';
import sendWhatsappConnectionDetails from 'my-phorest/gql/mutations/send-whatsapp-connection-details.graphql';
import whatsappConnectionsQuery from 'my-phorest/gql/queries/whatsapp-connections.graphql';
import { task, dropTask, timeout, didCancel } from 'ember-concurrency';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import config from 'my-phorest/config/environment';

const POLLING_LIMIT = 200; // requests
const VERIFICATION_CALL_INTERVAL = 2000; // [ms]

export default class WhatsappService extends Service {
  @service session;
  @service notifications;
  @service errorHandler;
  @service intl;
  @service('browser/window') window;

  @queryManager apollo;

  @tracked pollingCounter = 0;
  @tracked showCompleteSetupModal = false;
  @tracked isFbSdkForWhatsAppInjected = false;
  @tracked showAboutToLeavePhorestModal = false;

  get hasConnections() {
    const result = this.getWhatsAppConnectionsTask.lastSuccessful?.value;
    return result?.totalCount > 0;
  }

  get hasSuccessfulConnection() {
    const result = this.getWhatsAppConnectionsTask.lastSuccessful?.value;
    return result?.edges?.some((edge) => edge.node.status === 'FINISHED');
  }

  get successfulConnection() {
    const result = this.getWhatsAppConnectionsTask.lastSuccessful?.value;
    const successfulConnection = result?.edges?.find(
      (edge) => edge.node.status === 'FINISHED'
    );
    return successfulConnection?.node ?? null;
  }

  /**
   * Inits Facebook SDK by injecting it to our app (on demand) and configuring.
   * @returns {Promise}
   */
  initFacebookSdkForWhatsApp() {
    return new Promise((resolve) => {
      if (this.isFbSdkForWhatsAppInjected) {
        return resolve();
      }
      window.fbAsyncInit = () => {
        window.FB.init({
          appId: config.facebook.whatsappAppId, // Phorest FB App ID
          autoLogAppEvents: true,
          status: true,
          cookie: true, // enable cookies to allow the server to access the session
          xfbml: true, // parse social plugins on this page
          version: 'v22.0', // uses graph api version v22.0
        });
        this.isFbSdkForWhatsAppInjected = true;
        resolve();
      };
      this._injectFacebookSdk();
    });
  }

  /**
   * Opens Facebook SDK's for WhatsApp Embedded Signup.
   * @returns {Promise}
   */
  launchWhatsAppSignup() {
    return new Promise((resolve, reject) => {
      const configuration = {
        config_id: config.facebook.whatsappLoginConfigurationId, // Login Configuration ID
        response_type: 'code', // Must be set to 'code' for System User access token
        override_default_response_type: true,
        extras: {
          setup: {
            solutionID: config.facebook.infobipSolutionId, // Infobip Solution ID
          },
          featureType: 'WA_EMBEDDED_SIGNUP',
          sessionInfoVersion: '3',
        },
      };

      FB.login((response) => {
        if (response) {
          resolve(response);
        } else {
          reject(response);
        }
      }, configuration);
    });
  }

  @action
  onCloseSetupModal() {
    this.showCompleteSetupModal = false;

    this.verifyWhatsAppRegistration?.cancelAll();
    this.window.removeEventListener(
      'message',
      this.getWhatsAppDetailsTask.perform
    );
    this.pollingCounter = 0;
  }

  @dropTask
  *launchWhatsAppEmbeddedSignUpFlow() {
    this.showCompleteSetupModal = true;
    this.showAboutToLeavePhorestModal = false;
    this.window.addEventListener(
      'message',
      this.getWhatsAppDetailsTask.perform
    );

    try {
      yield Promise.all([
        this.launchWhatsAppSignup(),
        this.verifyWhatsAppRegistration.perform(),
      ]);
    } catch (error) {
      if (didCancel(error)) {
        this.onCloseSetupModal();
        return;
      }
      this.notifications.failure(
        this.intl.t('global.error-notification-title'),
        htmlSafe(error),
        { sticky: true }
      );
      this.errorHandler.handle(error);
    }
  }

  @task
  *getWhatsAppDetailsTask(event) {
    if (!event.origin.endsWith('facebook.com')) return;
    try {
      let parsedJson = JSON.parse(event.data);
      const { type, data: signupData = {} } = parsedJson;
      if (type === 'WA_EMBEDDED_SIGNUP') {
        const { waba_id, business_id, phone_number_id } = signupData;
        if (waba_id && business_id && phone_number_id) {
          yield this.sendWhatsappConnectionDetailsTask.perform(
            business_id,
            phone_number_id,
            waba_id
          );
        }
      }
    } catch (error) {
      if (error?.name === 'SyntaxError') {
        return;
      }
      this.notifications.failure(
        this.intl.t('global.error-notification-title'),
        htmlSafe(error),
        { sticky: true }
      );
      this.errorHandler.handle(error, { showError: false });
    }
  }

  @task
  *getWhatsAppConnectionsTask() {
    const connections = yield this.apollo.watchQuery(
      {
        query: whatsappConnectionsQuery,
        variables: {
          filterBy: {
            branchId: this.session.branchId,
          },
        },
        fetchPolicy: 'network-only',
      },
      'whatsappConnections'
    );
    return connections;
  }

  @dropTask
  *sendWhatsappConnectionDetailsTask(
    businessPortfolioId,
    phoneNumberId,
    wabaId
  ) {
    yield this.apollo.mutate(
      {
        mutation: sendWhatsappConnectionDetails,
        variables: {
          input: {
            branchId: this.session.branchId,
            businessPortfolioId,
            phoneNumberId,
            wabaId,
            accountId: this.session.accountId,
          },
        },
      },
      'sendWhatsappConnectionDetails'
    );
  }

  @task
  *verifyWhatsAppRegistration() {
    try {
      yield this.getWhatsAppConnectionsTask.perform();

      if (this.hasSuccessfulConnection) {
        this.showCompleteSetupModal = false;
      } else if (this.#stopPollingAfterLimit()) {
        yield timeout(VERIFICATION_CALL_INTERVAL);
        yield this.verifyWhatsAppRegistration.perform();
      }
    } catch (error) {
      if (!didCancel(error)) {
        this.errorHandler.handle(error);
      }
    }
  }

  /**
   * Injects loading script for FacebookSDK. We inject it asynchronously, on demand.
   * (not by injecting to every page as a typical way of doing these things)
   *
   * @private
   */
  _injectFacebookSdk() {
    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) {
        return;
      }
      js = d.createElement(s);
      js.id = id;
      js.async = true;
      js.defer = true;
      js.crossOrigin = 'anonymous';
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }

  #stopPollingAfterLimit() {
    this.pollingCounter++;
    return this.pollingCounter < POLLING_LIMIT;
  }
}
